import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import SeoSocialTags from '../components/SeoSocialTags';
import '../styles/BlogPrimitives.css';

const BLOG_POST_CANONICAL_URL = 'https://circumvent.io/blog/if-its-not-shipped-its-not-fixed';
const BLOG_POST_PUBLISHED_AT = '2026-08-12';
const BLOG_POST_READ_TIME = '5 min read';
const BLOG_POST_AUTHOR = 'Circumvent Team';
const FEATURE_IMAGE_SRC = '/images/blog/if-its-not-shipped-its-not-fixed.png';
const BLOG_POST_OG_IMAGE_URL = 'https://circumvent.io/images/blog/if-its-not-shipped-its-not-fixed.png';
const BLOG_POST_OG_TITLE = "If it's not shipped, it's not fixed | Circumvent";
const BLOG_POST_OG_DESCRIPTION =
  'Automated remediation often moves the queue onto engineering. Risk drops when a change is reviewed, merged, deployed, and verified; not when a bot opens a PR.';

const BlogIfItsNotShippedPage = () => (
  <>
    <Helmet>
      <title>{BLOG_POST_OG_TITLE}</title>
      <meta name="description" content={BLOG_POST_OG_DESCRIPTION} />
      <link rel="canonical" href={BLOG_POST_CANONICAL_URL} />
    </Helmet>
    <SeoSocialTags
      title={BLOG_POST_OG_TITLE}
      description={BLOG_POST_OG_DESCRIPTION}
      url={BLOG_POST_CANONICAL_URL}
      ogType="article"
      articlePublishedTime={`${BLOG_POST_PUBLISHED_AT}T00:00:00.000Z`}
      ogImageUrl={BLOG_POST_OG_IMAGE_URL}
    />

    <main className="page page--light blog-post-page">
      <section className="page-section page-section--white" aria-labelledby="post-heading">
        <article className="page-container blog-post">
          <div className="blog-post__header">
            <Link to="/blog" className="blog-crumb-link">
              <span aria-hidden="true">←</span> Blog
            </Link>
            <h1 id="post-heading" className="blog-post__title">
              If it&apos;s not shipped, it&apos;s not fixed
            </h1>
            <p className="blog-post__lead">
              Automated remediation promised to close the gap between detection and risk reduction. Too often it only
              moves the queue onto engineering, where fixes stall until they ship, or never ship at all.
            </p>

            <ul className="blog-meta-list blog-meta-list--header" aria-label="Post details">
              <li>Insights</li>
              <li>
                <time dateTime={BLOG_POST_PUBLISHED_AT}>12 Aug 2026</time>
              </li>
              <li>{BLOG_POST_READ_TIME}</li>
              <li>By {BLOG_POST_AUTHOR}</li>
            </ul>
          </div>

          <div className="blog-post__layout">
            <div className="blog-post__content">
              <figure className="blog-post__feature-image blog-post__feature-image--bleed">
                <img
                  src={FEATURE_IMAGE_SRC}
                  alt="Illustration of unfinished work piled on one side of a chasm, with a broken bridge blocking the way across."
                  width={1536}
                  height={864}
                  loading="eager"
                  decoding="async"
                />
              </figure>

              <p>
                Security vendors have spent years selling the same logic: if scanners can find issues at machine speed,
                fixes should ship at machine speed too. Open the pull request. Patch the image. Flip the config. Close
                the finding.
              </p>
              <p>
                On paper that sounds like progress. In practice, many security programs discover a harder truth. A bot
                PR is not a remediation. Risk drops when a change is reviewed, merged, deployed, and verified in the
                environments that matter. Until then, the finding is still open, even if a ticket says otherwise.
              </p>

              <h2 id="automation-moved-the-bottleneck">
                Automation did not remove the bottleneck. It moved it.
              </h2>
              <p>
                Engineering&apos;s primary job is to deliver product work that moves the business. Security work has to
                fit around that reality. When security consumes more than a thin slice of sprint capacity, product
                leadership pushes back, and for good reason. Capacity is finite.
              </p>
              <p>
                That constraint is not a culture failure. It is how delivery organizations stay solvent. So when security
                automation floods a team with pull requests, tickets, or &quot;one-click&quot; fixes, the bottleneck
                does not disappear. It shifts from &quot;we cannot find the issue&quot; to &quot;we cannot absorb the
                work.&quot;
              </p>
              <p>
                Security engineers feel this as backlog pressure and strained relationships with product teams. Security
                leaders see it later as low adoption, ignored integrations, and dashboards that claim progress that
                production never received.
              </p>
              <p>
                When capacity is scarce, the only workable strategy is ruthless focus: bring the highest-impact risks
                into the sprint, and accept that some lower-priority risks will wait. Automation that cannot respect that
                trade-off will be tuned out.
              </p>

              <h2 id="why-security-bot-prs-sit-untouched">Why security bot PRs sit untouched</h2>
              <p>
                Ask engineers why automated security PRs linger, and the answers are consistent. They are rarely about
                laziness.
              </p>

              <h3 id="the-fix-is-wrong">The fix is wrong, so trust erodes</h3>
              <p>
                Engineers will pick up bot-authored changes when those changes are usually correct. They stop when the
                default experience is cleanup work. Trust erodes over time: each bad automated fix teaches the team that
                security PRs are optional noise, until the stream is ignored altogether.
              </p>

              <h3 id="the-issue-is-not-real-enough">The issue is not real enough to spend a sprint on</h3>
              <p>
                Automated pipelines still open work for findings that should never have competed for engineering time:
              </p>
              <ul>
                <li>
                  Not deployed: container images or packages that are not running in the environments you protect.
                </li>
                <li>
                  Not reachable in practice: vulnerable library code for functions the application never calls.
                </li>
                <li>
                  Already mitigated: compensating controls the scanner cannot see, so the finding looks open while the
                  risk is already bounded.
                </li>
              </ul>
              <p>
                Each of those wastes the same scarce resource: human attention and trust inside a sprint.
              </p>

              <h3 id="the-change-lands-in-the-wrong-place">The change lands in the wrong place</h3>
              <p>
                Ownership matters as much as correctness. A fix that belongs in a shared infrastructure or platform repo
                often opens against a feature team&apos;s repository instead. That team may lack permission to land the
                change where it actually belongs. They become coordinators for work that was never theirs, which slows
                response and burns goodwill.
              </p>
              <p>
                The same failure shows up as ticket ping-pong. Work bounces between teams looking for an owner because
                ownership is unclear, or because the application, service, or resource is orphaned. Nobody merges. Nobody
                deploys. The finding ages while people argue about who owns it.
              </p>

              <h3 id="the-change-is-untested">The change is untested against how the team ships</h3>
              <p>
                Even a technically valid patch can stall if it ignores build pipelines, review norms, blast radius, or
                release cadence. Untested automation asks engineers to absorb regression risk on behalf of a tool. Most
                teams will not.
              </p>
              <p>
                Underneath all of this is a cultural rule many organizations already live by: if it is not in a sprint,
                it is not getting looked at. Security work that bypasses planning, ownership, and prioritization will
                lose to product delivery every time.
              </p>

              <h2 id="what-this-means-for-security-engineers">What this means for security engineers</h2>
              <p>
                Treat &quot;automated remediation&quot; as incomplete until the change is shipped and verified. Measure
                closure by deployment and residual risk, not by PRs opened or tickets created.
              </p>
              <p>
                Protect engineering trust. Prefer fewer, better-scoped remediation packages over high-volume bot activity.
                Validate reachability, deployment status, ownership, and compensating controls before you ask a team to
                spend sprint capacity.
              </p>
              <p>
                Route work to the team that can actually merge and operate the fix. Wrong-repo automation is operational
                debt dressed up as velocity.
              </p>

              <h2 id="what-security-leaders-should-evaluate">What security leaders should evaluate</h2>
              <p>
                When you assess remediation claims, ask questions that expose whether the product reduces risk or only
                accelerates ticket creation:
              </p>
              <ol>
                <li>
                  Does it filter for real exposure? Can it distinguish deployed from undeployed, used from unused,
                  mitigated from open?
                </li>
                <li>
                  Does it respect ownership? Does work land with the team and repository that can ship the change?
                </li>
                <li>
                  Does it fit how delivery already works? Tickets, channels, planning cycles, review queues, and release
                  paths, not a parallel security workflow nobody lives in.
                </li>
                <li>
                  How does it protect engineering capacity? What stops low-value findings from consuming sprint time?
                </li>
                <li>
                  What is the definition of done? Opened PR, merged PR, deployed change, or verified risk reduction?
                </li>
              </ol>
              <p>
                If a vendor cannot answer those clearly, you are buying throughput against a backlog, not outcomes against
                risk.
              </p>

              <h2 id="closing-the-loop">Closing the loop</h2>
              <p>
                Detection without delivery is incomplete. Automation without context is often counterproductive. The
                programs that improve posture are the ones that put the right risks, with the right owners, into the same
                systems teams already use to plan and ship.
              </p>
              <p>
                At Circumvent we treat remediation as unfinished until work is owned, actioned, and on a path to
                production. Insights should compete for sprint capacity on merit: verified risk, clear ownership, and a
                fix path engineers can trust.
              </p>
              <p>If it&apos;s not shipped, it&apos;s not fixed.</p>

              <div className="blog-post__cta">
                <h3>Remediation that lands in the sprint</h3>
                <p>Talk to us about remediation that lands where engineers already plan and ship.</p>
                <Link to="/#contact" className="btn btn-solid">
                  Book a demo
                </Link>
              </div>

              <Link to="/blog" className="blog-crumb-link">
                <span aria-hidden="true">←</span> Blog
              </Link>
            </div>

            <aside className="blog-post__sidebar" aria-label="Post navigation">
              <nav className="blog-toc" aria-label="On this page">
                <p className="blog-toc__title">On this page</p>
                <ul className="blog-toc__list">
                  <li className="blog-toc__item">
                    <a className="blog-toc__link" href="#automation-moved-the-bottleneck">
                      Automation moved the bottleneck
                    </a>
                  </li>
                  <li className="blog-toc__item">
                    <a className="blog-toc__link" href="#why-security-bot-prs-sit-untouched">
                      Why security bot PRs sit untouched
                    </a>
                  </li>
                  <li className="blog-toc__item">
                    <a className="blog-toc__link" href="#what-this-means-for-security-engineers">
                      What this means for security engineers
                    </a>
                  </li>
                  <li className="blog-toc__item">
                    <a className="blog-toc__link" href="#what-security-leaders-should-evaluate">
                      What security leaders should evaluate
                    </a>
                  </li>
                  <li className="blog-toc__item">
                    <a className="blog-toc__link" href="#closing-the-loop">
                      Closing the loop
                    </a>
                  </li>
                </ul>
              </nav>
            </aside>
          </div>
        </article>
      </section>
    </main>
  </>
);

export default BlogIfItsNotShippedPage;
